import { useTranslation } from 'react-i18next'
import { ExternalLink } from 'lucide-react'
import { Card } from '@/components/ui'
import { formatBaht } from '@/lib/money'
import type { SubscriptionPricing, SubscriptionTier } from '@/hooks/useSubscription'

// The published rate card is the standard tier. The invitation tier is a
// single free bracket, so rendering ITS brackets would replace the price list
// with one "0+ / ฿0 / ฿0" row; instead an invitation account reads the same
// card with no bracket highlighted (design B, "no special free banner").
const RATE_CARD_TIER = 'standard'

const PRICING_URL = 'https://flowrent.io/pricing'

export interface SubscriptionPricingTableProps {
  pricing: SubscriptionPricing
  tier: SubscriptionTier
  roomCount: number
}

/**
 * EP-144 T144-008: the tier brackets, monthly and annual side by side.
 *
 * Annual is display only in v1 (Q-4b, G-1): no annual invoice is generated.
 * The bracket bounds are printed as the server sent them, so the first bracket
 * reads from 0 rooms, which is what the engine actually charges (C-14).
 */
export function SubscriptionPricingTable({ pricing, tier, roomCount }: SubscriptionPricingTableProps) {
  const { t } = useTranslation('settings')
  const brackets = pricing[RATE_CARD_TIER]?.brackets ?? []

  if (brackets.length === 0) return null

  const isActive = (min: number, max: number | null) =>
    tier === RATE_CARD_TIER && roomCount >= min && (max === null || roomCount <= max)

  return (
    <Card title={t('subscription.pricing.title')} className="mt-6">
      <div className="overflow-x-auto">
        <table className="table table-sm text-sm">
          <caption className="sr-only">{t('subscription.pricing.title')}</caption>
          <thead>
            <tr>
              <th scope="col">{t('subscription.pricing.rooms')}</th>
              <th scope="col">{t('subscription.pricing.monthly')}</th>
              <th scope="col">{t('subscription.pricing.annual')}</th>
            </tr>
          </thead>
          <tbody>
            {brackets.map((bracket) => {
              const active = isActive(bracket.min, bracket.max)
              return (
                <tr
                  key={`${bracket.min}-${bracket.max ?? 'open'}`}
                  aria-current={active ? 'true' : undefined}
                  className={active ? 'bg-base-200 font-medium' : undefined}
                >
                  <th scope="row" className="font-normal">
                    {bracket.max === null
                      ? t('subscription.pricing.range_open', { min: bracket.min })
                      : t('subscription.pricing.range', { min: bracket.min, max: bracket.max })}
                  </th>
                  <td className="tabular-nums">{formatBaht(bracket.monthly, { decimals: 0 })}</td>
                  <td className="tabular-nums">{formatBaht(bracket.annual, { decimals: 0 })}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <a
        href={PRICING_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="link link-primary mt-3 inline-flex items-center gap-1 text-sm"
      >
        {t('subscription.pricing.link')}
        <ExternalLink className="w-3 h-3" aria-hidden="true" />
      </a>
    </Card>
  )
}
